'use client'

import { Divider } from '@mantine/core'
import { type PropsWithChildren, useLayoutEffect, useRef, useState } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { AccountLink } from './AccountLink'
import { HeaderSearchButton } from './HeaderSearchButton'
import { HeaderSearchProvider } from './HeaderSearchContext'
import { HeaderSearchFallback } from './HeaderSearchFallback'
import { HeaderSearchPortal } from './HeaderSearchPortal'
import { ExpandedHeader } from './ExpandedHeader'
import { Logo } from './Logo'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { getSiteHeaderConfig } from '@/lib/siteHeader/getSiteHeaderConfig'
import classes from './SiteHeader.module.css'

/** How far the page must scroll before the expanded header shrinks to the compact bar. */
const EXPANDED_HEADER_COLLAPSE_OFFSET = 200

interface SiteHeaderProps extends PropsWithChildren {
    /**
     * True when the request already has session cookies.
     * Used so the account destination does not flash Sign in while `fetchUser` runs.
     */
    initiallyLoggedIn: boolean
}

/**
 * Sticky General Medicine site header for Store 2.0.
 *
 * Visibility and search permissions come from the current URL. Universal
 * Search stays centered in the compact header on desktop and docks to the
 * bottom of the screen on mobile.
 *
 * Some routes use an expanded header that later collapses. Search starts in
 * that expanded header and moves into the compact bar after a short scroll.
 * Expanded content is the shop title, search slot, and pills (`ExpandedHeader`).
 */
export function SiteHeader({ children, initiallyLoggedIn }: SiteHeaderProps) {
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const config = getSiteHeaderConfig(pathname)
    const { isSm: isMobile } = useBreakpoint()
    const searchQuery = searchParams.has('query') ? (searchParams.get('query') ?? '') : undefined
    const [hasScrolledPastExpandedHeader, setHasScrolledPastExpandedHeader] = useState(false)
    const compactSearchTargetRef = useRef<HTMLDivElement>(null)
    const expandedSearchTargetRef = useRef<HTMLDivElement>(null)
    const showSearch = isMobile
        ? config.showSearchOnMobile || config.showSearchTriggerOnMobile
        : config.showSearchOnDesktop
    const isHeaderSearchAvailable = !config.logoOnly && config.showSearchTriggerOnMobile

    /**
     * Runs before paint so the enlarged expanded-header search does not flash
     * on a page that is already scrolled. Listens only when the expanded
     * header is in use.
     */
    useLayoutEffect(() => {
        if (!config.showExpandedHeader) {
            setHasScrolledPastExpandedHeader(false)
            return
        }

        const updateHeader = () => setHasScrolledPastExpandedHeader(window.scrollY >= EXPANDED_HEADER_COLLAPSE_OFFSET)

        updateHeader()
        window.addEventListener('scroll', updateHeader, { passive: true })
        return () => window.removeEventListener('scroll', updateHeader)
    }, [config.showExpandedHeader])

    if (!config.visible) {
        return children
    }

    const isHeaderExpanded = config.showExpandedHeader && !hasScrolledPastExpandedHeader
    // Placeholder div that should display search. Not a ref to the search input.
    const searchTargetRef = isHeaderExpanded ? expandedSearchTargetRef : compactSearchTargetRef

    return (
        <HeaderSearchProvider isAvailable={isHeaderSearchAvailable}>
            <div className={classes.root}>
                <div className={`${classes.shell} ${config.showExpandedHeader ? classes.shellExpanded : ''}`}>
                    <header className={`${classes.header} ${isHeaderExpanded ? classes.headerExpanded : ''}`}>
                        <div
                            className={[
                                classes.headerContent,
                                config.showSearchTriggerOnMobile ? classes.headerContentWithSearchTrigger : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                        >
                            <div className={classes.logoSlot}>
                                <Logo />
                            </div>
                            <div
                                className={[
                                    classes.searchSlot,
                                    config.showSearchOnMobile ? classes.searchOnMobile : '',
                                    config.showSearchOnDesktop ? classes.searchOnDesktop : '',
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                                ref={compactSearchTargetRef}
                            >
                                {!config.logoOnly && showSearch && !isHeaderExpanded ? (
                                    <HeaderSearchFallback searchQuery={searchQuery} />
                                ) : null}
                            </div>
                            <div className={classes.accountSlot}>
                                {!config.logoOnly && config.showSearchTriggerOnMobile && <HeaderSearchButton />}
                                {!config.logoOnly ? (
                                    <AccountLink initiallyLoggedIn={initiallyLoggedIn} />
                                ) : null}
                            </div>
                        </div>
                        {config.showBottomDivider && (
                            <Divider
                                visibleFrom="sm"
                                color="var(--gm-gray-1)"
                                aria-hidden
                            />
                        )}
                    </header>
                    {config.showExpandedHeader && (
                        <ExpandedHeader
                            searchTargetRef={expandedSearchTargetRef}
                            showSearchSlot={isHeaderExpanded && showSearch}
                            searchQuery={searchQuery}
                        />
                    )}
                    {!config.logoOnly && showSearch && (
                        <HeaderSearchPortal
                            // Remount when expanded-header routes turn on/off so search
                            // does not FLIP from the compact bar into the hero.
                            key={config.showExpandedHeader ? 'expanded-header' : 'compact-header'}
                            targetRef={searchTargetRef}
                            searchQuery={searchQuery}
                            placement={config.showSearchTriggerOnMobile ? 'trigger' : 'auto'}
                        />
                    )}
                    <div className={classes.content}>{children}</div>
                </div>
            </div>
        </HeaderSearchProvider>
    )
}
